// Incident: branch clock out of sync — NTP chain (Tier 3 · after Domain 07).
//
// Iloilo NOC (src/domains/d07/topologies.js operational) with the branch time design used for
// this incident: SRV-NTP (stratum 1) → R1 (stratum 2) → R2 (stratum 3). The branch router takes
// its time from the hub, not from the server farm. Since a "configuration clean-up", R2's log
// times start with "*" (not authoritative) and drift by minutes, so the overnight outage cannot be
// correlated with the NOC's logs. One cause is rolled:
//   - R1 lost "ntp server 10.70.20.30": the hub is unsynchronised (stratum 16), so R2, which
//     still points at R1, has nothing valid to follow. Both clocks free-run.
//   - R2 points at 10.70.12.2, its OWN WAN address, instead of R1 10.70.12.1: it asks itself.
//
// Real NTP state (src/engine/net/services.js ntpState): reachability, the upstream's own
// synchronisation and stratum, hop by hop. (The D05/D07 finals roll an NTP server address with no
// server behind it; Mission 09 configures NTP from nothing: not these.)
import { note } from '../ui/widgets.js';
import { nmsConsole } from '../ui/svcWidgets.js';
import { operational, apply, CRED, MON, NTP } from '../domains/d07/topologies.js';
import { ios, ntp } from '../domains/d07/helpers.js';
import { logEvent } from '../engine/net/services.js';
import { nocLayout, pollingAndSite, nmsLooked } from './mgmt-common.js';
import { forcedRoll, rollGroups, groupRollValidator } from '../core/faultRoll.js';

const HUB = '10.70.12.1';
const ntpOf = (n, id) => n.devices[id].ntp;
export const FAULTS = {
  'hub-upstream-removed': { group: 1, device: 'R1', layers: ['NTP hierarchy'], title: 'R1 lost "ntp server 10.70.20.30" in the clean-up: the hub fell to stratum 16 (unsynchronised), and R2, still pointing at R1, had no valid time source either', apply: (n) => { ntpOf(n, 'R1').servers = []; } },
  'branch-self': { group: 1, device: 'R2', layers: ['NTP hierarchy'], title: 'R2 pointed NTP at 10.70.12.2, its own WAN address, instead of R1 10.70.12.1, so it asked itself for the time and never synchronised', apply: (n) => { ntpOf(n, 'R2').servers = ['10.70.12.2']; } },
};
const GROUPS = [1];
const validForced = groupRollValidator(FAULTS, GROUPS);
/** The incident's time design: R2 follows R1; both clocks drift a little when free-running. */
function network() {
  const n = operational();
  apply('R2', [`no ntp server ${NTP}`, `ntp server ${HUB}`])(n);
  n.devices.R1.clockSkewMs = 2.4e3;
  n.devices.R2.clockSkewMs = -(7 * 60e3 + 42e3);
  return n;
}
/** The overnight branch outage as logged on R2 (time stamps from R2's own clock). */
function outage(n) {
  logEvent(n, 'R2', '%LINK-3-UPDOWN: Interface GigabitEthernet0/1, changed state to down', -(6 * 3600e3 + 2 * 60e3));
  logEvent(n, 'R2', '%LINK-3-UPDOWN: Interface GigabitEthernet0/1, changed state to up', -(5 * 3600e3 + 56 * 60e3));
}

// ---------------------------------------------------------------- checks (read live state)
/** The time chain works: R1 stratum 2 from SRV-NTP, R2 stratum 3 from R1. */
export function chainSynced(rt) {
  const a = ntp(rt, 'R1'); const b = ntp(rt, 'R2');
  return a.synced && a.stratum === 2 && a.server === NTP && b.synced && b.stratum === 3 && b.server === HUB;
}
/** The time design exactly: R1 → SRV-NTP only, R2 → R1 only, no "ntp master" anywhere (no fake reference). */
export function timeDesigned(rt) {
  const r1 = ntpOf(rt.net, 'R1') || {}; const r2 = ntpOf(rt.net, 'R2') || {};
  return JSON.stringify(r1.servers || []) === JSON.stringify([NTP]) && JSON.stringify(r2.servers || []) === JSON.stringify([HUB]) && !r1.master && !r2.master
    && rt.net.devices.R1.timestamps && rt.net.devices.R2.timestamps;
}
/** Polling, syslog destinations and the site are untouched. */
export const restIntact = (rt) => pollingAndSite(rt);

export default {
  id: 'ntp-sync', version: 1, kind: 'TROUBLESHOOTING', type: 'Incident Lab', implemented: true,
  title: 'Branch Log Times Drift',
  incident: {
    severity: 'P3', tier: 3, related: 'd07', relatedLabel: 'Domain 07 · Infrastructure Management (NTP)', site: 'Iloilo NOC ⇄ Guimaras', exam: ['CCNA', 'CCNP ENCOR'],
    summary: 'The branch router\'s log times are marked "*" and minutes off, so last night\'s outage cannot be lined up with the NOC\'s logs.',
  },
  objective: 'Follow the NTP chain from the branch router to the time server, find the hop that is broken, restore the time design, and prove both routers are synchronised at the right stratum.',
  faultCatalog: FAULTS,
  network,
  validForced,
  setup(rt) {
    const forced = forcedRoll(rt);
    rt.data.faults = forced && validForced(forced) ? forced : rollGroups(FAULTS, GROUPS, () => rt.random());
    for (const k of rt.data.faults) FAULTS[k].apply(rt.net);
    outage(rt.net);
  },
  layout: nocLayout(network(), { title: 'Iloilo NOC — time chain SRV-NTP → R1 → R2' }),
  linkLights: true,
  stages: [
    {
      id: 'investigate', phase: 'TICKET → TROUBLESHOOT', title: 'Follow the time chain', devices: ['R2', 'R1', 'PC-OPS'],
      ticket: { id: '7730', priority: 3, from: 'NOC shift lead', site: 'GUIMARAS', opened: '09:20', subject: 'R2 log times wrong, outage timeline does not line up', body: 'For the 03:12 branch outage report we need R1 and R2 logs on one timeline. R2\'s messages on SRV-MON are marked with "*" and are several minutes off from R1\'s. This started after the configuration clean-up last week (CHG-7329).' },
      content: `<p>"*" in front of a time means the clock is not authoritative: no NTP synchronisation.</p>
<table><tr><th>Time design</th><th></th></tr>
<tr><td>SRV-NTP 10.70.20.30</td><td>Stratum 1 (GPS)</td></tr>
<tr><td>R1 (hub)</td><td><code>ntp server 10.70.20.30</code> → stratum 2</td></tr>
<tr><td>R2 (branch)</td><td><code>ntp server 10.70.12.1</code> (R1 G0/3) → stratum 3. No <code>ntp master</code> anywhere.</td></tr></table>
<ol><li><b>Symptom</b>: <code>show clock</code> on R2 and R1, and the NMS console log times.</li>
<li><b>Follow the chain</b>: <code>show ntp associations</code> and <code>show ntp status</code> on R2. Which address does it ask, and what stratum does that peer report? Then the same on R1.</li>
<li><b>Configuration</b>: <code>show running-config | include ntp</code> on the hop that is wrong.</li></ol>
<p>Enable secret: <code>${CRED.enable}</code>.</p>`,
      widgets: [nmsConsole({ id: 'inc-ntp-nms', monIp: MON.ip, community: MON.community, title: 'NMS CONSOLE · SRV-MON', required: false })],
      evidence: [
        { id: 'clock', text: 'R2: clock / NTP status checked', match: ios('R2', ['show clock', 'show ntp status']) },
        { id: 'r2', text: 'R2: NTP associations read', match: ios('R2', 'show ntp associations') },
        { id: 'r1', text: 'R1: NTP associations or status read (the next hop up the chain)', match: ios('R1', ['show ntp associations', 'show ntp status']) },
      ],
      tasks: [
        { id: 'sync', text: 'R1 synchronised to SRV-NTP at stratum 2; R2 synchronised to R1 at stratum 3', check: chainSynced, fail: 'The time chain is still broken somewhere between SRV-NTP, R1 and R2.' },
        { id: 'design', text: 'NTP matches the design (R1 → 10.70.20.30, R2 → 10.70.12.1, no "ntp master"; timestamps on)', check: timeDesigned, fail: 'NTP does not match the design. "ntp master" only makes a router claim its own clock is right, and R2 must follow R1, not the server farm.' },
        { id: 'rest', text: 'SNMP polling, syslog and the site are unchanged', check: restIntact, fail: 'Something outside NTP changed.' },
      ],
      question: 'Which line of "show ntp associations" told you whether the problem was on R2 or one hop further up?',
      hints: [
        'In <code>show ntp associations</code>, the "st" column is the peer\'s stratum. 16 means the peer is not synchronised itself. A peer address that is your own interface can never give you time.',
        'NTP is a chain: R2 can only be right if R1 is right. If R1 shows stratum 16 too, look at R1\'s own <code>ntp server</code> line.',
        `Restore the broken hop, for example on R1 <code>ntp server ${NTP}</code>, or on R2 <code>no ntp server 10.70.12.2</code> and <code>ntp server ${HUB}</code>. Synchronisation follows within a few commands; check with <code>show ntp status</code>.`,
      ],
    },
    {
      id: 'document', phase: 'DOCUMENT', title: 'Close the incident', workspace: false,
      widgets: [note({ id: 'inc-ntp-note', title: 'INCIDENT REPORT · #7730', prompt: 'Which hop of the time chain was broken and why, the evidence from both routers, the fix, and how you proved both are synchronised at the right stratum.', min: 60 })],
    },
  ],
  review: (rt) => `<p><b>What was actually broken:</b></p><ol>${(rt.data.faults || []).map((k) => `<li>${FAULTS[k]?.title || k}</li>`).join('')}</ol><p>NTP time flows down a chain, one stratum per hop. A device is only as good as the peer it follows, so troubleshoot from the symptom upwards: whom does this device ask, and is that peer synchronised itself? <code>ntp master</code> would hide the problem by making a router trust its own clock.</p>`,
};
